// client/src/components/Sidebar.js
import {
    Box, VStack, Button, Text, Spinner, IconButton,
    Input, Tooltip, useDisclosure, AlertDialog,
    AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
    AlertDialogBody, AlertDialogFooter,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { useState, useEffect, useRef, useCallback } from "react";

const API = process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';
const NAVBAR_HEIGHT = 65;

export default function Sidebar({ isOpen, onNewProject, onSelectProject, activeProjectId }) {
    const [projects, setProjects] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Rename state
    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const renameDebounceRef = useRef(null);

    // Delete confirmation dialog
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [deletingProject, setDeletingProject] = useState(null);
    const cancelDeleteRef = useRef();

    // ── Fetch initial 10 projects ──────────────────────────────────
    const fetchProjects = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API}/api/projects`, {
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setProjects(data.projects);
            setHasMore(data.hasMore);
            setNextCursor(data.nextCursor);
        } catch (err) {
            console.error('Failed to fetch projects:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Fetch on sidebar open
    useEffect(() => {
        if (isOpen) fetchProjects();
    }, [isOpen, fetchProjects]);

    // ── Load more ───────────────────────────────────
    const loadMore = async () => {
        if (!hasMore || isLoadingMore || !nextCursor) return;
        setIsLoadingMore(true);
        try {
            const res = await fetch(`${API}/api/projects?cursor=${nextCursor}`, {
                credentials: 'include',
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);

            setProjects(prev => [...prev, ...data.projects]); // append
            setHasMore(data.hasMore);
            setNextCursor(data.nextCursor);
        } catch (err) {
            console.error('Failed to load more projects:', err);
        } finally {
            setIsLoadingMore(false);
        }
    };

    // ── Rename ─────────────────────────────────────────────────────
    const startEditing = (e, project) => {
        e.stopPropagation(); // don't trigger project select
        setEditingId(project.id);
        setEditingName(project.name);
    };

    const handleRenameChange = (value) => {
        setEditingName(value);

        // Debounce DB call by 800ms
        if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current);
        renameDebounceRef.current = setTimeout(async () => {
            if (!value.trim()) return;
            try {
                await fetch(`${API}/api/projects/${editingId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ name: value.trim() }),
                });
                // Update local state — no refetch needed
                setProjects(prev =>
                    prev.map(p => p.id === editingId ? { ...p, name: value.trim() } : p)
                );
            } catch (err) {
                console.error('Rename failed:', err);
            }
        }, 800);
    };

    const commitRename = () => {
        if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current);
        if (editingName.trim()) {
            // Final save if debounce hasn't fired yet
            fetch(`${API}/api/projects/${editingId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ name: editingName.trim() }),
            }).catch(err => console.error('Final rename failed:', err));

            setProjects(prev =>
                prev.map(p => p.id === editingId ? { ...p, name: editingName.trim() } : p)
            );
        }
        setEditingId(null);
        setEditingName('');
    };

    const cancelRename = (e) => {
        e?.stopPropagation();
        if (renameDebounceRef.current) clearTimeout(renameDebounceRef.current);
        setEditingId(null);
        setEditingName('');
    };

    // ── Delete ─────────────────────────────────────────────────────
    const confirmDelete = (e, project) => {
        e.stopPropagation();
        setDeletingProject(project);
        onDeleteOpen();
    };

    const handleDelete = async () => {
        if (!deletingProject) return;
        try {
            const res = await fetch(`${API}/api/projects/${deletingProject.id}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Delete failed');

            // Remove from local state — no refetch needed
            setProjects(prev => prev.filter(p => p.id !== deletingProject.id));

            // If deleted project was active, trigger new project
            if (deletingProject.id === activeProjectId) {
                onNewProject();
            }
        } catch (err) {
            console.error('Delete failed:', err);
        } finally {
            onDeleteClose();
            setDeletingProject(null);
        }
    };

    return (
        <>
            <Box
                position="fixed"
                top={`${NAVBAR_HEIGHT}px`}
                left="0"
                height={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
                width="250px"
                bg="white"
                borderRight="1px solid transparent"
                zIndex="900"
                transform={isOpen ? "translateX(0)" : "translateX(-100%)"}
                transition="transform 0.25s ease-out"
                p="20px"
            //     display="flex"
            // flexDirection="column"
            // p="16px"
            // overflowY="auto"
            >
                <VStack align="start" spacing={5}>
                    <Button
                        width="100%"
                        bg="#CF008A"         
                        color="#FFFFFF"      
                        borderRadius="12px"
                        _hover={{ bg: "#CF008A" }}
                        _active={{ bg: "#CF008A" }}
                        onClick={onNewProject}
                    >
                        New Chat
                    </Button>
                    <Text
                        mt={4}
                        color="#242424"
                        textTransform="uppercase"
                        fontSize="11px"
                        fontStyle="normal"
                        fontWeight="700"
                        lineHeight="16px"
                    >
                        Recents
                    </Text>

                    {isLoading ? (
                        <Spinner size="sm" color="#CF008A" alignSelf="center" mt={4} />
                    ) : (
                        <VStack align="start" spacing={2} width="100%">
                            {projects.length === 0 && (
                                <Text fontSize="sm" color="#9E9E9E" px={2} mt={2}>
                                    No projects yet
                                </Text>
                            )}

                            {projects.map((project) => {
                                const isActive = project.id === activeProjectId;
                                const isEditing = editingId === project.id;

                                // <Button
                                //     key={index}
                                //     // variant="ghost"
                                //     justifyContent="flex-start"
                                //     width="100%"
                                //     borderRadius="12px"
                                //     fontSize="sm"
                                //     color="#4F4F4F"
                                //     bg="transparent"
                                //     _hover={{ bg: "transparent" }}
                                // >
                                //     {project}
                                // </Button>
                                return (
                                    <Box
                                        key={project.id}
                                        width="100%"
                                        borderRadius="12px"
                                        bg={isActive ? "#FFF0FA" : "transparent"}
                                        border={isActive ? "1px solid #F9C8EA" : "1px solid transparent"}
                                        px={3}
                                        py={2}
                                        color="#4F4F4F"
                                        cursor="pointer"
                                        role="group"
                                        onClick={() => !isEditing && onSelectProject(project.id, project.name)}
                                        _hover={{ bg: isActive ? "#FFF0FA" : "#F7F7F7" }}
                                        transition="background 0.15s ease"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        //justifyContent="flex-start"
                                        minH="36px"
                                    >
                                        {isEditing ? (
                                            // ── Inline rename input ──
                                            <Box display="flex" alignItems="center" width="100%" gap={1}>
                                                <Input
                                                    value={editingName}
                                                    onChange={(e) => handleRenameChange(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') commitRename();
                                                        if (e.key === 'Escape') cancelRename();
                                                    }}
                                                    size="xs"
                                                    borderRadius="6px"
                                                    autoFocus
                                                    onClick={(e) => e.stopPropagation()}
                                                    flex={1}
                                                    borderColor="#CF008A"
                                                    _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
                                                />
                                                <IconButton
                                                    icon={<CheckIcon />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="#CF008A"
                                                    onClick={(e) => { e.stopPropagation(); commitRename(); }}
                                                    aria-label="Confirm rename"
                                                />
                                                <IconButton
                                                    icon={<CloseIcon />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color="#9E9E9E"
                                                    onClick={cancelRename}
                                                    aria-label="Cancel rename"
                                                />
                                            </Box>
                                        ) : (
                                            // ── Project name + action buttons ──
                                            <>
                                                <Text
                                                    fontSize="sm"
                                                    color={isActive ? "#CF008A" : "#4F4F4F"}
                                                    fontWeight={isActive ? "600" : "400"}
                                                    noOfLines={1}
                                                    flex={1}
                                                    mr={1}
                                                >

                                                    {project.name}
                                                </Text>

                                                {/* Action buttons — visible on hover */}
                                                <Box
                                                    display="flex"
                                                    gap={0.5}
                                                    opacity={0}
                                                    _groupHover={{ opacity: 1 }}
                                                    transition="opacity 0.15s ease"
                                                >
                                                    <Tooltip label="Rename" size="sm">
                                                        <IconButton
                                                            icon={<EditIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            color="#9E9E9E"
                                                            _hover={{ color: "#CF008A", bg: "transparent" }}
                                                            onClick={(e) => startEditing(e, project)}
                                                            aria-label="Rename project"
                                                        />
                                                    </Tooltip>
                                                    <Tooltip label="Delete" size="sm">
                                                        <IconButton
                                                            icon={<DeleteIcon />}
                                                            size="xs"
                                                            variant="ghost"
                                                            color="#9E9E9E"
                                                            _hover={{ color: "#E53E3E", bg: "transparent" }}
                                                            onClick={(e) => confirmDelete(e, project)}
                                                            aria-label="Delete project"
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}
                                    </Box>
                                );
                            })}

                            {/* Load More */}
                            {hasMore && (
                                <Button
                                    width="100%"
                                    variant="ghost"
                                    size="sm"
                                    color="#9E9E9E"
                                    borderRadius="12px"
                                    mt={2}
                                    isLoading={isLoadingMore}
                                    onClick={loadMore}
                                    _hover={{ bg: "#F7F7F7", color: "#4F4F4F" }}
                                >
                                    Load more
                                </Button>
                            )}
                        </VStack>
                    )}
                </VStack>
            </Box >

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                isOpen={isDeleteOpen}
                leastDestructiveRef={cancelDeleteRef}
                onClose={onDeleteClose}
                isCentered
            >
                <AlertDialogOverlay />
                <AlertDialogContent borderRadius="16px">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        Delete Project
                    </AlertDialogHeader>
                    <AlertDialogBody>
                        Are you sure you want to delete <strong>{deletingProject?.name}</strong>?
                        This will permanently delete all messages, suggestions and reports in this project.
                    </AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelDeleteRef} onClick={onDeleteClose} borderRadius="12px">
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleDelete}
                            ml={3}
                            borderRadius="12px"
                        >
                            Delete
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

        </>
    );
}
